import React from "react";
import PageLayout, { SectionReveal } from "../components/PageLayout";
import PageHero from "../components/PageHero";
import PageBanner from "../components/PageBanner";
import { MessageSquare, Globe, Code2, Smartphone, Languages, Zap, Check, ArrowRight } from "lucide-react";

const ChatBotPage = () => {
  const features = [
    {
      icon: Globe,
      title: "Web Sitesi Widget'ı",
      desc: "Web sitenize dakikalar içinde ekleyebileceğiniz, markanıza özel tasarlanmış sohbet widget'ı. Ziyaretçilerinizle anında etkileşime geçin.",
    },
    {
      icon: Smartphone,
      title: "Mobil Uygulama SDK'sı",
      desc: "iOS ve Android uygulamalarınıza kolayca entegre edebileceğiniz SDK. Push notification, sesli mesaj ve canlı destek özellikleri dahil.",
    },
    {
      icon: Code2,
      title: "REST API & Webhook",
      desc: "Kendi uygulamanıza özel entegrasyon için kapsamlı REST API. Webhook'lar ile gerçek zamanlı veri akışı ve özel iş akışları.",
    },
    {
      icon: MessageSquare,
      title: "Çok Kanallı Destek",
      desc: "Web, mobil, WhatsApp, Telegram ve Instagram'da aynı AI botu çalıştırın. Tüm kanallardan gelen mesajları tek panelden yönetin.",
    },
    {
      icon: Languages,
      title: "Çok Dilli Bot",
      desc: "Botunuz 30'dan fazla dilde hizmet verebilir. Kullanıcının dilini otomatik algılar ve o dilde yanıt verir.",
    },
    {
      icon: Zap,
      title: "Canlı Desteğe Aktarım",
      desc: "Bot çözemediğinde, konuşma geçmişiyle birlikte canlı temsilciye sorunsuz aktarım. Kullanıcı aynı pencerede devam eder.",
    },
  ];

  const hero = (
    <PageHero
      eyebrow="Ürün"
      title="Uygulamanız İçin Chat Bot"
      subtitle="Web sitenize, mobil uygulamanıza veya özel yazılımınıza entegre edebileceğiniz, markanıza özel yapay zeka sohbet botu."
    />
  );

  return (
    <PageLayout hero={hero}>
      <PageBanner src="/images/pages/chatbot-ui.jpg" alt="Chat Bot arayüzü" height="360px" />
      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[28px] font-bold mb-4" style={{ color: "var(--fg)" }}>Kendi botunuzu oluşturun</h2>
            <p className="text-[15px] leading-relaxed max-w-2xl mx-auto" style={{ color: "var(--fg)", opacity: 0.85 }}>
              İster web sitenize bir widget ekleyin, ister mobil uygulamanıza SDK ile entegre edin. Hiç kod yazmadan veya geliştirici ekibinizle tam özelleştirme yaparak.
            </p>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((f) => (
                <div key={f.title} className="p-6 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(75,111,212,0.12)", color: "var(--brand-cyan)" }}>
                    <f.icon size={22} />
                  </div>
                  <h3 className="text-[16px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{f.title}</h3>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.82 }}>{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img
                src="/images/pages/messaging-apps.jpg"
                alt="Çok kanallı mesajlaşma"
                className="w-full h-auto object-cover"
                style={{ maxHeight: "320px" }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-1)" }}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[28px] font-bold mb-10 text-center" style={{ color: "var(--fg)" }}>Entegrasyon Seçenekleri</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { title: "Hazır Widget", desc: "Tek satır JavaScript kodu ile web sitenize ekleyin. Renk, logo ve yazı tipi özelleştirmesi panelden yapılır.", code: "<script src=\"...\">" },
                { title: "SDK Entegrasyonu", desc: "iOS, Android, React Native ve Flutter için native SDK'lar. Minimum kod ile maksimum kontrol.", code: "npm install @verasist/sdk" },
                { title: "Özel API", desc: "Tam kontrol isteyenler için REST ve WebSocket API. Kendi arayüzünüzü tasarlayın, AI motorunu biz sağlayalım.", code: "POST /api/v1/chat" },
              ].map((opt) => (
                <div key={opt.title} className="p-6 rounded-2xl transition-colors duration-200" style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}>
                  <h3 className="text-[17px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{opt.title}</h3>
                  <p className="text-[13.5px] leading-relaxed mb-4" style={{ color: "var(--fg)", opacity: 0.82 }}>{opt.desc}</p>
                  <code className="text-[12px] px-2 py-1 rounded block" style={{ background: "var(--bg)", color: "var(--brand-cyan)" }}>{opt.code}</code>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionReveal>

      <SectionReveal>
        <section className="py-16 px-6 text-center">
          <a href="https://app.verasist.ai" className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-[15px] transition-all duration-200" style={{ background: "var(--brand-navy)", color: "#fff" }}>
            Chat Bot Oluşturun <ArrowRight size={18} />
          </a>
        </section>
      </SectionReveal>
    </PageLayout>
  );
};

export default ChatBotPage;
